import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Trash2, Send, Download, Loader2, Store, Bot, Cpu, Webhook as WebhookIcon, GitBranch, ChevronRight } from 'lucide-react';
import { haptic } from '../../telegram';
import { marketplaceApi, type DetailListingCard, type InstallCard } from '../../api/marketplaceApi';

// Иконка/подпись для карточки установки — по виду исходного листинга (kind), как в каталоге.
const INSTALL_KIND_ICON: Record<string, any> = { agent: Bot, mcp: Cpu, webhook: WebhookIcon, pipeline: GitBranch };
const INSTALL_KIND_LABEL: Record<string, string> = { agent: 'Агент', mcp: 'MCP-подключение', webhook: 'Вебхук', pipeline: 'Сценарий' };
// Куда вести при клике на карточку установки — по типу реально созданного ресурса (см. installCard() на бэкенде).
function installTarget(it: InstallCard): string | null {
  const r = it.primaryResource;
  if (!r) return null;
  if (r.type === 'pipeline') return '/automation/' + r.id;
  if (r.type === 'agent') return '/settings/agents';
  if (r.type === 'mcp' || r.type === 'webhook') return '/settings/connections';
  if (r.type === 'skill' || r.type === 'document') return '/settings/knowledge';
  return null;
}

export function MarketplaceMine() {
  const nav = useNavigate();
  const [tab, setTab] = useState<'listings' | 'installs'>('listings');
  const [items, setItems] = useState<DetailListingCard[]>([]);
  const [installs, setInstalls] = useState<InstallCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const loadListings = () => {
    setLoading(true);
    marketplaceApi.getMine().then((r) => { setItems(r || []); setLoading(false); }).catch(() => setLoading(false));
  };
  const loadInstalls = () => {
    setLoading(true);
    marketplaceApi.getInstalls().then((r) => { setInstalls(r || []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { loadListings(); }, []);

  const switchTab = (t: 'listings' | 'installs') => { haptic('light'); setTab(t); if (t === 'installs') loadInstalls(); else loadListings(); };

  const remove = async (id: string) => { haptic('heavy'); setBusy(id); try { await marketplaceApi.deleteListing(id); setItems((prev) => prev.filter((i) => i.id !== id)); } catch {} finally { setBusy(null); } };
  const submit = async (id: string) => { haptic('medium'); setBusy(id); try { await marketplaceApi.submitListing(id); haptic('heavy'); loadListings(); } catch {} finally { setBusy(null); } };
  const removeInstall = async (id: string) => { haptic('heavy'); setBusy(id); try { await marketplaceApi.deleteInstall(id); setInstalls((prev) => prev.filter((i) => i.id !== id)); } catch {} finally { setBusy(null); } };
  const openInstall = (it: InstallCard) => {
    const target = installTarget(it);
    if (!target) return;
    haptic('light');
    nav(target);
  };

  const statusText = (s: string) => s === 'published' ? 'Опубликован' : s === 'pending' ? 'На модерации' : s === 'rejected' ? 'Отклонён' : s === 'draft' ? 'Черновик' : s === 'disabled' ? 'Отключён' : s;
  const statusCls = (s: string) => s === 'published' ? 'on' : s === 'rejected' || s === 'disabled' ? 'err' : 'run';

  return (
    <div className="rf-detail">
      <div className="rf-topbar"><button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button><div className="rf-topbar-title">Мои публикации</div></div>

      <div className="rf-detail-scroll">
        <div className="rf-info"><Store /><div>{tab === 'listings'
          ? <><b>Публикации</b><br />Сценарии и агенты, которых вы выложили в общий каталог. Черновик можно отправить на модерацию — после проверки он станет доступен другим пользователям.</>
          : <><b>Установленные</b><br />Сценарии и агенты, которых вы установили из каталога.</>}</div></div>
        <div className="rf-seg">
          <button className={'rf-seg-btn' + (tab === 'listings' ? ' active' : '')} onClick={() => switchTab('listings')}>Публикации</button>
          <button className={'rf-seg-btn' + (tab === 'installs' ? ' active' : '')} onClick={() => switchTab('installs')}>Установленные</button>
        </div>

        {loading ? (
          <div className="rf-loading"><div className="rf-spin" /><p>Загрузка…</p></div>
        ) : tab === 'listings' ? (
          items.length === 0 ? (
            <div className="rf-empty"><div className="rf-empty-ic"><Package /></div><h3>Нет публикаций</h3><p>Опубликуйте свой сценарий или агента</p></div>
          ) : (
            <div className="rf-rows rf-stagger">
              {items.map((it) => (
                <div key={it.id} className="rf-card rf-acard">
                  <div className="rf-conn-row">
                    <div className="rf-row-ic"><Package /></div>
                    <div className="rf-conn-info"><div className="rf-conn-name">{it.title}</div><div className="rf-conn-sub">{it.summary}</div></div>
                    <span className={'rf-pill ' + statusCls(it.status)}><span className="dot" />{statusText(it.status)}</span>
                  </div>
                  {it.rejected_reason && <div className="rf-hint rf-mt10">Причина: {it.rejected_reason}</div>}
                  <div className="rf-acts">
                    {(it.status === 'draft' || it.status === 'rejected') && (
                      <button className="rf-btn sm primary rf-grow" onClick={() => submit(it.id)} disabled={busy === it.id}>{busy === it.id ? <Loader2 className="spin" /> : <Send />}На модерацию</button>
                    )}
                    <button className="rf-btn sm danger icon" title="Удалить" onClick={() => remove(it.id)} disabled={busy === it.id}>{busy === it.id ? <Loader2 className="spin" /> : <Trash2 />}</button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          installs.length === 0 ? (
            <div className="rf-empty"><div className="rf-empty-ic"><Download /></div><h3>Ничего не установлено</h3><p>Установите сценарий из каталога</p></div>
          ) : (
            <div className="rf-rows rf-stagger">
              {installs.map((it) => {
                const Icon = (it.kind && INSTALL_KIND_ICON[it.kind]) || Download;
                const kindLabel = (it.kind && INSTALL_KIND_LABEL[it.kind]) || '';
                const clickable = !!installTarget(it);
                const errSuffix = it.status === 'error' && it.error ? ' · Ошибка: ' + it.error : '';
                return (
                  <div key={it.id} className="rf-card rf-conn">
                    <div className="rf-conn-row" style={clickable ? { cursor: 'pointer' } : undefined} onClick={clickable ? () => openInstall(it) : undefined}>
                      <div className="rf-row-ic"><Icon /></div>
                      <div className="rf-conn-info">
                        <div className="rf-conn-name">{it.title}</div>
                        {(kindLabel || errSuffix) && <div className="rf-conn-sub">{kindLabel}{errSuffix}</div>}
                      </div>
                      {clickable && <div className="rf-row-right"><ChevronRight /></div>}
                      <button className="rf-btn sm danger icon" title="Удалить" onClick={(e) => { e.stopPropagation(); removeInstall(it.id); }} disabled={busy === it.id}>{busy === it.id ? <Loader2 className="spin" /> : <Trash2 />}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}
